import {memo, useCallback, useEffect, useState} from 'react';

import {useDebouncedCallback} from '../../shared/useDebounce';
import {C, uid} from '../constants';
import {FORMULAS} from '../formulaUtils';

const VarRow = memo(function VarRow({variable, onUpdateSample, onDelete}) {
    const [localSample, setLocalSample] = useState(variable.sample);

    useEffect(() => {
        setLocalSample(variable.sample);
    }, [variable.sample]);

    const debouncedUpdate = useDebouncedCallback(
        (value) => onUpdateSample(variable.id, value),
        300,
    );

    const handleChange = useCallback((event) => {
        const value = event.target.value;
        setLocalSample(value);
        debouncedUpdate(value);
    }, [debouncedUpdate]);

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 18px',
                gap: 4,
                alignItems: 'center',
            }}>
            <div
                // Native title so users can hover a truncated variable chip
                // and see the full {{name}} without opening anything.
                title={`{{${variable.name}}}`}
                style={{
                    fontSize: 10,
                    fontWeight: 500,
                    color: C.blue,
                    fontFamily: 'monospace',
                    background: C.blueL,
                    padding: '3px 6px',
                    borderRadius: 4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'default',
                }}>{`{{${variable.name}}}`}</div>
            <input
                value={localSample}
                onChange={handleChange}
                style={{
                    fontFamily: 'inherit',
                    fontSize: 10,
                    padding: '3px 6px',
                    border: `1px solid ${C.border}`,
                    borderRadius: 4,
                    background: C.surfaceRaised,
                    color: C.ink,
                }}
            />
            <button
                onClick={() => onDelete(variable.id)}
                style={{
                    border: 'none',
                    background: 'transparent',
                    color: C.textSoft,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: 12,
                    lineHeight: 1,
                }}>
                ×
            </button>
        </div>
    );
});

const sanitizeVarName = (raw) => {
    // Strip non-alphanumeric/underscore chars, ensure starts with letter/underscore
    let name = raw.trim().replace(/\s+/g, '_').toLowerCase().replace(/[^a-z0-9_]/g, '');
    if (name && /^[0-9]/.test(name)) name = `_${name}`;
    return name;
};

export default function VarsPanel({vars, setVars}) {
    const [open, setOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [newSample, setNewSample] = useState('');

    const updateSample = (id, value) =>
        setVars((currentVars) =>
            currentVars.map((variable) => (variable.id === id ? {...variable, sample: value} : variable)),
        );

    const deleteVar = (id) =>
        setVars((currentVars) => currentVars.filter((variable) => variable.id !== id));

    const addVar = () => {
        const sanitized = sanitizeVarName(newName);
        if (!sanitized) return;

        setVars((currentVars) => [
            ...currentVars,
            {
                id: `v${uid()}`,
                name: sanitized,
                sample: newSample.trim(),
            },
        ]);
        setNewName('');
        setNewSample('');
    };

    return (
        <div style={{borderBottom: `1px solid ${C.border}`}}>
            <div
                onClick={() => setOpen((current) => !current)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setOpen((current) => !current);
                    }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={open}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 1.75rem',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}>
                <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                    <span
                        style={{
                            fontSize: 10,
                            fontWeight: 500,
                            color: C.textSoft,
                            textTransform: 'uppercase',
                            letterSpacing: '.07em',
                        }}>
                        Product Variables
                    </span>
                    <span
                        style={{
                            fontSize: 9,
                            padding: '1px 6px',
                            borderRadius: 8,
                            background: C.blueL,
                            color: C.blue,
                            fontWeight: 500,
                        }}>
                        {vars.length}
                    </span>
                    <span
                        style={{
                            fontSize: 9,
                            padding: '1px 6px',
                            borderRadius: 8,
                            background: C.greenL,
                            color: C.green,
                            fontWeight: 500,
                        }}>
                        {FORMULAS.length} fn
                    </span>
                </div>
                <span
                    style={{
                        fontSize: 11,
                        color: C.textSoft,
                        transform: open ? 'rotate(180deg)' : 'none',
                        transition: 'transform .2s',
                        display: 'inline-block',
                    }}>
                    ▾
                </span>
            </div>
            {open && (
                <div style={{padding: '0 1.75rem .9rem', height: '400px', overflowY: 'scroll'}}>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 18px',
                            gap: 4,
                            marginBottom: 4,
                        }}>
                        {['Variable', 'Sample', ''].map((heading, index) => (
                            <span
                                key={index}
                                style={{
                                    fontSize: 8,
                                    color: C.textSoft,
                                    textTransform: 'uppercase',
                                    letterSpacing: '.05em',
                                }}>
                                {heading}
                            </span>
                        ))}
                    </div>
                    <div style={{display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 7}}>
                        {vars.map((variable) => (
                            <VarRow
                                key={variable.id}
                                variable={variable}
                                onUpdateSample={updateSample}
                                onDelete={deleteVar}
                            />
                        ))}
                    </div>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 18px',
                            gap: 4,
                            alignItems: 'center',
                            paddingTop: 6,
                            borderTop: `1px dashed ${C.border}`,
                        }}>
                        <input
                            value={newName}
                            onChange={(event) => setNewName(event.target.value)}
                            onKeyDown={(event) => event.key === 'Enter' && addVar()}
                            placeholder="var_name"
                            style={{
                                fontFamily: 'monospace',
                                fontSize: 10,
                                padding: '3px 6px',
                                border: `1px solid ${C.border2}`,
                                borderRadius: 4,
                                // Subtle blue tint hints this input is for a
                                // variable name (mirrors the blueL pill used
                                // on existing variable chips above).
                                background: C.blueL,
                                color: C.blue,
                                outline: 'none',
                            }}
                        />
                        <input
                            value={newSample}
                            onChange={(event) => setNewSample(event.target.value)}
                            onKeyDown={(event) => event.key === 'Enter' && addVar()}
                            placeholder="sample value"
                            style={{
                                fontFamily: 'inherit',
                                fontSize: 10,
                                padding: '3px 6px',
                                border: `1px solid ${C.border2}`,
                                borderRadius: 4,
                                // Match the sample-input background used on
                                // existing variable rows so the two areas
                                // read as the same visual language.
                                background: C.surfaceRaised,
                                color: C.ink,
                                outline: 'none',
                            }}
                        />
                        <button
                            onClick={addVar}
                            style={{
                                width: 18,
                                height: 18,
                                borderRadius: 3,
                                border: 'none',
                                background: C.blue,
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: 13,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 700,
                            }}>
                            +
                        </button>
                    </div>
                    <div style={{fontSize: 9.5, color: C.textSoft, marginTop: 8, lineHeight: 1.5}}>
                        Type{' '}
                        <code
                            style={{
                                fontFamily: 'monospace',
                                background: `${C.blue}12`,
                                padding: '0 3px',
                                borderRadius: 3,
                                color: C.blue,
                            }}>
                            {'{{'}
                        </code>{' '}
                        in any field to insert a variable or formula.
                    </div>
                </div>
            )}
        </div>
    );
}
